/**
 * @page ProzessAbteilungDetailPage
 * @route /prozesse/abteilungen/:slug
 * @description Abteilung-Detail — Breadcrumb, KPI Cards, Baum-Ansicht (Bereiche → Prozesse).
 * @roles admin, ops_manager
 * @i18n prozesse.*
 * @endpoints GET /api/prozesse/abteilungen/:slug
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import BackLink from '../components/BackLink';
import { Building2, ChevronRight, FileText, CheckCircle, Clock, Archive, FolderOpen, ChevronDown, Plus, X, Loader2 } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

const STATUS_MAP = {
  entwurf:     { labelKey: 'prozesse.status.entwurf',     color: '#9CA3AF', bg: '#F3F4F6' },
  in_review:   { labelKey: 'prozesse.status.in_review',   color: '#F59E0B', bg: '#FEF3C7' },
  freigegeben: { labelKey: 'prozesse.status.freigegeben', color: '#338edc', bg: '#EBF4FD' },
  aktiv:       { labelKey: 'prozesse.status.aktiv',        color: '#10B981', bg: '#D1FAE5' },
  archiviert:  { labelKey: 'prozesse.status.archiviert',   color: '#6B7280', bg: '#F3F4F6' },
};

function StatusBadge({ status, t }) {
  const s = STATUS_MAP[status] || STATUS_MAP.entwurf;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ backgroundColor: s.bg, color: s.color }}>
      {t(s.labelKey) || status}
    </span>
  );
}

function formatDate(d) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function ProzessAbteilungDetailPage() {
  const { slug } = useParams();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedBereiche, setExpandedBereiche] = useState(new Set());
  const [showBereichModal, setShowBereichModal] = useState(false);
  const [newProzessForBereich, setNewProzessForBereich] = useState(null);

  const loadData = () => {
    setLoading(true);
    fetch(`${API}/api/prozesse/abteilungen/${slug}`, { credentials: 'include' })
      .then(r => {
        if (!r.ok) throw new Error('Not found');
        return r.json();
      })
      .then(d => {
        // Backend returns { abteilung, bereiche, prozesse } as flat arrays
        // Build tree: nest prozesse under their bereich
        const allProzesse = d.prozesse || [];
        const bereicheRaw = d.bereiche || [];
        const bereicheWithProcs = bereicheRaw.map(b => ({
          ...b,
          prozesse: allProzesse.filter(p => p.bereich_id === b.id),
        }));
        // Prozesse without bereich go into an "Uncategorized" section
        const unassigned = allProzesse.filter(p => !p.bereich_id);
        // Compute stats
        const stats = {
          gesamt: allProzesse.length,
          aktiv: allProzesse.filter(p => p.status === 'aktiv').length,
          in_review: allProzesse.filter(p => p.status === 'in_review').length,
          entwurf: allProzesse.filter(p => p.status === 'entwurf').length,
        };
        setData({ abteilung: d.abteilung, bereiche: bereicheWithProcs, unassigned, stats });
        // Expand all areas by default
        const ids = new Set(bereicheRaw.map(b => b.id));
        if (unassigned.length > 0) ids.add('__unassigned__');
        setExpandedBereiche(ids);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const toggleBereich = (id) => {
    setExpandedBereiche(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin h-6 w-6 border-2 border-[#338edc] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 md:p-8">
        <div className="text-center py-20">
          <p className="text-sm text-[var(--text-tertiary,#6B7280)]">{t('prozesse.abteilung.notFound') || 'Department not found'}</p>
          <div className="mt-3 flex justify-center">
            <BackLink to="/prozesse/abteilungen" label={t('common.backToList')} />
          </div>
        </div>
      </div>
    );
  }

  const abt = data.abteilung || {};
  const bereiche = data.bereiche || [];
  const unassigned = data.unassigned || [];
  const stats = data.stats || {};

  const kpis = [
    { label: t('prozesse.stats.gesamt'),    value: stats.gesamt ?? 0,    Icon: FileText,    color: 'text-[#338edc]' },
    { label: t('prozesse.stats.aktiv'),     value: stats.aktiv ?? 0,     Icon: CheckCircle, color: 'text-[#10B981]' },
    { label: t('prozesse.stats.inReview'),  value: stats.in_review ?? 0, Icon: Clock,       color: 'text-[#F59E0B]' },
    { label: t('prozesse.stats.entwuerfe'), value: stats.entwurf ?? 0,   Icon: Archive,     color: 'text-[#6366F1]' },
  ];

  return (
    <div className="p-6 md:p-8 space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-xs text-[var(--text-tertiary,#6B7280)]">
        <Link to="/prozesse" className="hover:text-[#338edc] transition-colors">{t('prozesse.title')}</Link>
        <ChevronRight size={12} />
        <Link to="/prozesse/abteilungen" className="hover:text-[#338edc] transition-colors">{t('prozesse.abteilungen')}</Link>
        <ChevronRight size={12} />
        <span className="text-[var(--text-primary,#1A1A2E)] font-medium">{abt.name}</span>
      </nav>

      {/* Header */}
      <div className="flex items-center gap-3">
        <Building2 size={24} className="text-[var(--text-secondary,#4B5563)]" />
        <div>
          <h1 className="text-xl font-semibold text-[var(--text-primary,#1A1A2E)]">{abt.name}</h1>
          {abt.beschreibung && (
            <p className="text-xs text-[var(--text-secondary,#4B5563)] mt-0.5">{abt.beschreibung}</p>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpis.map((kpi, i) => (
          <div key={i} className="rounded-2xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-5">
            <div className="flex items-center gap-2 mb-3">
              <kpi.Icon size={16} className={kpi.color} />
              <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary,#4B5563)]">{kpi.label}</span>
            </div>
            <div className="text-2xl font-bold text-[var(--text-primary,#1A1A2E)]">{kpi.value}</div>
          </div>
        ))}
      </div>

      {/* Tree View: Bereiche → Prozesse */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)]">
            {t('prozesse.bereiche')} ({bereiche.length})
          </h2>
          <button
            onClick={() => setShowBereichModal(true)}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-xs font-semibold transition-colors"
          >
            <Plus size={14} />
            {t('prozesse.bereich.neu', 'New Area')}
          </button>
        </div>

        {bereiche.length === 0 ? (
          <div className="rounded-2xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-8 text-center">
            <div className="w-12 h-12 mx-auto rounded-2xl bg-[var(--bg-inset,#F8F9FC)] flex items-center justify-center mb-3">
              <FolderOpen size={20} className="text-[var(--text-muted,#9CA3AF)]" />
            </div>
            <p className="text-sm text-[var(--text-tertiary,#6B7280)] mb-4">{t('prozesse.abteilung.noBereiche') || 'No areas yet'}</p>
            <button
              onClick={() => setShowBereichModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold transition-colors"
            >
              <Plus size={14} />
              {t('prozesse.bereich.neu', 'New Area')}
            </button>
          </div>
        ) : (
          bereiche.map(bereich => {
            const isOpen = expandedBereiche.has(bereich.id);
            const procs = bereich.prozesse || [];

            return (
              <div key={bereich.id} className="rounded-2xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] overflow-hidden">
                {/* Bereich Header */}
                <div className="w-full flex items-center justify-between px-5 py-4 hover:bg-[var(--bg-hover,#FAFAFE)] transition-colors">
                  <button
                    onClick={() => toggleBereich(bereich.id)}
                    className="flex items-center gap-3 flex-1 min-w-0 text-left"
                  >
                    <FolderOpen size={18} className="text-[var(--text-secondary,#4B5563)] flex-shrink-0" />
                    <div className="min-w-0">
                      <span className="text-sm font-medium text-[var(--text-primary,#1A1A2E)]">{bereich.name}</span>
                      {bereich.beschreibung && (
                        <p className="text-xs text-[var(--text-tertiary,#6B7280)] mt-0.5 truncate">{bereich.beschreibung}</p>
                      )}
                    </div>
                  </button>
                  <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                    <span className="text-xs text-[var(--text-tertiary,#6B7280)]">
                      {procs.length} {t('prozesse.stats.gesamt')}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); setNewProzessForBereich(bereich.id); }}
                      title={t('prozesse.proc.neu', 'New Process')}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-[10px] border border-[var(--border-card,#E5E7EB)] hover:border-[#338edc] hover:text-[#338edc] text-xs font-medium text-[var(--text-secondary,#4B5563)] transition-colors"
                    >
                      <Plus size={12} />
                      {t('prozesse.proc.neu', 'New Process')}
                    </button>
                    <button
                      onClick={() => toggleBereich(bereich.id)}
                      className="text-[var(--text-tertiary,#6B7280)]"
                    >
                      <ChevronDown
                        size={16}
                        className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                  </div>
                </div>

                {/* Prozesse List */}
                {isOpen && (
                  <div className="border-t border-[var(--border-card,#E5E7EB)]">
                    {procs.length === 0 ? (
                      <div className="px-5 py-6 flex flex-col items-center text-center gap-3">
                        <p className="text-xs text-[var(--text-tertiary,#6B7280)]">
                          {t('prozesse.abteilung.noProcesses') || 'No processes in this area'}
                        </p>
                        <button
                          onClick={() => setNewProzessForBereich(bereich.id)}
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-xs font-semibold transition-colors"
                        >
                          <Plus size={12} />
                          {t('prozesse.proc.neu', 'New Process')}
                        </button>
                      </div>
                    ) : (
                      procs.map(proc => (
                        <div
                          key={proc.id}
                          onClick={() => navigate(`/prozesse/${proc.id}`)}
                          className="flex items-center justify-between px-5 py-3 border-b last:border-b-0 border-[var(--border-table,#F3F4F6)] hover:bg-[var(--bg-hover,#FAFAFE)] cursor-pointer transition-colors"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-1.5 h-1.5 rounded-full bg-[var(--text-tertiary,#6B7280)] flex-shrink-0" />
                            <span className="text-sm text-[var(--text-primary,#1A1A2E)] truncate">{proc.titel}</span>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                            <StatusBadge status={proc.status} t={t} />
                            <span className="text-[10px] text-[var(--text-tertiary,#6B7280)]">v{proc.aktuelle_version}</span>
                            <span className="text-[10px] text-[var(--text-tertiary,#6B7280)]">{formatDate(proc.aktualisiert_am)}</span>
                            <ChevronRight size={14} className="text-[var(--text-tertiary,#6B7280)]" />
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}

        {/* #1725 P1-Polish: Sammelkarte "Ohne Bereich" — Prozesse mit bereich_id=NULL,
            die sonst durch alle Bereichs-Karten fallen und im Drilldown unsichtbar wären. */}
        {unassigned.length > 0 && (() => {
          const isOpen = expandedBereiche.has('__unassigned__');
          return (
            <div className="rounded-2xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] overflow-hidden">
              <button
                onClick={() => toggleBereich('__unassigned__')}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-[var(--bg-hover,#FAFAFE)] transition-colors text-left"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Archive size={18} className="text-[var(--text-secondary,#4B5563)] flex-shrink-0" />
                  <span className="text-sm font-medium text-[var(--text-primary,#1A1A2E)]">
                    {t('prozesse.ohneBereich') || 'Ohne Bereich'}
                  </span>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                  <span className="text-xs text-[var(--text-tertiary,#6B7280)]">
                    {unassigned.length} {t('prozesse.stats.gesamt')}
                  </span>
                  <ChevronDown size={16} className={`text-[var(--text-tertiary,#6B7280)] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </div>
              </button>
              {isOpen && (
                <div className="border-t border-[var(--border-card,#E5E7EB)]">
                  {unassigned.map(proc => (
                    <div
                      key={proc.id}
                      onClick={() => navigate(`/prozesse/${proc.id}`)}
                      className="flex items-center justify-between px-5 py-3 border-b last:border-b-0 border-[var(--border-table,#F3F4F6)] hover:bg-[var(--bg-hover,#FAFAFE)] cursor-pointer transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-1.5 h-1.5 rounded-full bg-[var(--text-tertiary,#6B7280)] flex-shrink-0" />
                        <span className="text-sm text-[var(--text-primary,#1A1A2E)] truncate">{proc.titel}</span>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                        <StatusBadge status={proc.status} t={t} />
                        <span className="text-[10px] text-[var(--text-tertiary,#6B7280)]">v{proc.aktuelle_version}</span>
                        <span className="text-[10px] text-[var(--text-tertiary,#6B7280)]">{formatDate(proc.aktualisiert_am)}</span>
                        <ChevronRight size={14} className="text-[var(--text-tertiary,#6B7280)]" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {showBereichModal && abt.id && (
        <NewBereichModal
          t={t}
          abteilungId={abt.id}
          onClose={() => setShowBereichModal(false)}
          onCreated={() => { setShowBereichModal(false); loadData(); }}
        />
      )}

      {newProzessForBereich !== null && abt.id && (
        <NewProzessModal
          t={t}
          abteilungId={abt.id}
          bereichId={newProzessForBereich}
          onClose={() => setNewProzessForBereich(null)}
          onCreated={(id) => { setNewProzessForBereich(null); navigate(`/prozesse/${id}`); }}
        />
      )}
    </div>
  );
}

const TYP_OPTIONS = [
  { value: 'standard',         labelKey: 'prozesse.typ.standard' },
  { value: 'kundenspezifisch', labelKey: 'prozesse.typ.kundenspezifisch' },
  { value: 'ausnahme',         labelKey: 'prozesse.typ.ausnahme' },
];

function NewProzessModal({ t, abteilungId, bereichId, onClose, onCreated }) {
  const [titel, setTitel] = useState('');
  const [typ, setTyp] = useState('standard');
  const [beschreibung, setBeschreibung] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!titel.trim()) return;
    setSaving(true);
    setError('');
    try {
      const body = {
        titel: titel.trim(),
        abteilung_id: abteilungId,
        bereich_id: bereichId || null,
        typ,
      };
      if (beschreibung.trim()) body.beschreibung = beschreibung.trim();
      const res = await fetch(`${API}/api/prozesse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const d = await res.json();
        onCreated(d.prozess?.id || d.id);
      } else {
        const d = await res.json().catch(() => ({}));
        setError(d.error || 'Error');
      }
    } catch { setError('Network error'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] shadow-xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card,#E5E7EB)]">
          <h2 className="text-base font-semibold text-[var(--text-primary,#1A1A2E)]">{t('prozesse.proc.neu', 'New Process')}</h2>
          <button onClick={onClose} className="text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-primary)]"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="text-xs text-[#EF4444] bg-[#EF44440D] border border-[#EF444433] rounded-lg px-3 py-2">{error}</div>
          )}
          <div>
            <label className="block text-xs font-semibold text-[var(--text-secondary,#4B5563)] mb-1.5">
              {t('prozesse.proc.title', 'Title')} *
            </label>
            <input
              type="text"
              value={titel}
              onChange={e => setTitel(e.target.value)}
              placeholder={t('prozesse.proc.titlePlaceholder', 'e.g. Inbound receiving process')}
              className="w-full px-3 py-2 rounded-[10px] border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-input,#fff)] text-sm text-[var(--text-primary,#1A1A2E)] focus:outline-none focus:border-[#338edc]"
              autoFocus
              required
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[var(--text-secondary,#4B5563)] mb-1.5">
              {t('prozesse.proc.type', 'Type')}
            </label>
            <select
              value={typ}
              onChange={e => setTyp(e.target.value)}
              className="w-full px-3 py-2 rounded-[10px] border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-input,#fff)] text-sm text-[var(--text-primary,#1A1A2E)] focus:outline-none focus:border-[#338edc]"
            >
              {TYP_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{t(o.labelKey, o.value)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[var(--text-secondary,#4B5563)] mb-1.5">
              {t('prozesse.proc.description', 'Description')}
            </label>
            <textarea
              value={beschreibung}
              onChange={e => setBeschreibung(e.target.value)}
              placeholder={t('prozesse.proc.descriptionPlaceholder', 'Short description (optional)...')}
              rows={3}
              className="w-full px-3 py-2 rounded-[10px] border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-input,#fff)] text-sm text-[var(--text-primary,#1A1A2E)] focus:outline-none focus:border-[#338edc] resize-none"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 rounded-[10px] border border-[var(--border-card,#E5E7EB)] text-sm font-medium text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-hover,#FAFAFE)]">
              {t('common.cancel', 'Cancel')}
            </button>
            <button type="submit" disabled={saving || !titel.trim()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold disabled:opacity-50 transition-colors">
              {saving && <Loader2 size={14} className="animate-spin" />}
              {t('prozesse.proc.create', 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function NewBereichModal({ t, abteilungId, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/prozesse/bereiche`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          abteilung_id: abteilungId,
          name: name.trim(),
          beschreibung: beschreibung.trim() || null,
        }),
      });
      if (res.ok) {
        onCreated();
      } else {
        const d = await res.json().catch(() => ({}));
        setError(d.error || 'Error');
      }
    } catch { setError('Network error'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] shadow-xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card,#E5E7EB)]">
          <h2 className="text-base font-semibold text-[var(--text-primary,#1A1A2E)]">{t('prozesse.bereich.neu', 'New Area')}</h2>
          <button onClick={onClose} className="text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-primary)]"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="text-xs text-[#EF4444] bg-[#EF44440D] border border-[#EF444433] rounded-lg px-3 py-2">{error}</div>
          )}
          <div>
            <label className="block text-xs font-semibold text-[var(--text-secondary,#4B5563)] mb-1.5">
              {t('prozesse.bereich.name', 'Name')} *
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={t('prozesse.bereich.namePlaceholder', 'e.g. Wareneingang, Versand, Kommissionierung')}
              className="w-full px-3 py-2 rounded-[10px] border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-input,#fff)] text-sm text-[var(--text-primary,#1A1A2E)] focus:outline-none focus:border-[#338edc]"
              autoFocus
              required
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[var(--text-secondary,#4B5563)] mb-1.5">
              {t('prozesse.bereich.beschreibung', 'Description')}
            </label>
            <textarea
              value={beschreibung}
              onChange={e => setBeschreibung(e.target.value)}
              placeholder={t('prozesse.bereich.beschreibungPlaceholder', 'Short description of this area...')}
              rows={3}
              className="w-full px-3 py-2 rounded-[10px] border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-input,#fff)] text-sm text-[var(--text-primary,#1A1A2E)] focus:outline-none focus:border-[#338edc] resize-none"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 rounded-[10px] border border-[var(--border-card,#E5E7EB)] text-sm font-medium text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-hover,#FAFAFE)]">
              {t('common.cancel', 'Cancel')}
            </button>
            <button type="submit" disabled={saving || !name.trim()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold disabled:opacity-50 transition-colors">
              {saving && <Loader2 size={14} className="animate-spin" />}
              {t('prozesse.bereich.create', 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
